import React, { useMemo } from 'react';
import {
  TIMELINE_HEADER_HEIGHT,
  TIMELINE_HORIZONTAL_PADDING,
} from '../constants';
import { getVisibleFrames } from '../utils/get-visible-frames';
import {
  getItemLeftOffset,
  getItemRoundedPosition,
  getItemWidth,
} from '../utils/position-utils';
import {
  useAllItems,
  useFps,
  useTimelineContext,
  useTracks,
  useCurrentStateAsRef,
} from '../utils/use-context';
import { DtoContentType } from '@/shared/constants';
import { ITEM_COLORS } from '../constants';
import { useAssetDropPreview } from './asset-drop-preview-provider';
import { remainOriginalTrackHeights } from './utils/drag/remain-original-track-heights';
import { TRACK_DIVIDER_HEIGHT } from '../state/items';
import { isIncomingPreviewCurrent } from '@waka/compat/incoming-preview';

interface AssetDropPreviewProps {
  timelineWidth: number;
}

const getColorByContentType = (contentType: DtoContentType): string => {
  switch (contentType) {
    case DtoContentType.video:
      return ITEM_COLORS.video;
    case DtoContentType.audio:
      return ITEM_COLORS.audio;
    case DtoContentType.image:
      return ITEM_COLORS.image;
    default:
      return '#888888';
  }
};

const DEFAULT_TRACK_HEIGHT = 48;

export const AssetDropPreview: React.FC<AssetDropPreviewProps> = ({
  timelineWidth,
}) => {
  const { previewState } = useAssetDropPreview();
  const { durationInFrames } = useTimelineContext();
  const { fps } = useFps();
  const { tracks } = useTracks();
  const { items } = useAllItems();
  const currentState = useCurrentStateAsRef();

  const visibleFrames = useMemo(
    () =>
      getVisibleFrames({
        fps,
        totalDurationInFrames: durationInFrames,
      }),
    [fps, durationInFrames],
  );

  // 트랙 레이아웃 계산
  const tracksAndLayout = useMemo(() => {
    return remainOriginalTrackHeights({
      originalTracks: tracks,
      originalItems: items,
      newTracks: tracks,
      newItems: items,
    });
  }, [tracks, items]);

  if (
    !previewState ||
    !isIncomingPreviewCurrent(currentState.current, previewState)
  ) {
    return null;
  }

  const timelineItemLeft = getItemLeftOffset({
    timelineWidth,
    totalDurationInFrames: visibleFrames,
    from: previewState.frame,
  });

  const timelineItemWidth = getItemWidth({
    itemDurationInFrames: previewState.durationInFrames,
    timelineWidth,
    totalDurationInFrames: visibleFrames,
  });

  const { roundedLeft, width: roundedWidth } = getItemRoundedPosition(
    timelineItemLeft,
    timelineItemWidth,
  );

  // 트랙 위치 계산
  let top = 0;
  let height = DEFAULT_TRACK_HEIGHT;
  let isNewTrack = false;

  const trackTargetType = previewState.trackTargetType;

  if (trackTargetType === 'create-at-top') {
    // 최상단에 새 트랙 생성: 첫 번째 트랙 위에 표시
    top = -DEFAULT_TRACK_HEIGHT - TRACK_DIVIDER_HEIGHT;
    height = DEFAULT_TRACK_HEIGHT;
    isNewTrack = true;
  } else if (trackTargetType === 'create-at-bottom') {
    // 최하단에 새 트랙 생성: 마지막 트랙 아래에 표시
    if (tracksAndLayout.length > 0) {
      const lastTrack = tracksAndLayout[tracksAndLayout.length - 1];
      if (lastTrack) {
        top = lastTrack.top + lastTrack.height + TRACK_DIVIDER_HEIGHT;
      }
    } else {
      top = 0;
    }
    height = DEFAULT_TRACK_HEIGHT;
    isNewTrack = true;
  } else if (trackTargetType === 'create-between') {
    // 해당 트랙 자리에 새 트랙 프리뷰 표시 (기존 트랙들은 아래로 밀린 것처럼)
    const insertAtIndex = previewState.insertAtIndex ?? 0;
    const targetTrack = tracksAndLayout[insertAtIndex];
    if (targetTrack) {
      // 해당 트랙 자리에 프리뷰 표시
      top = targetTrack.top;
      height = targetTrack.height;
    } else if (
      insertAtIndex === tracksAndLayout.length &&
      tracksAndLayout.length
    ) {
      const last = tracksAndLayout[tracksAndLayout.length - 1]!;
      top = last.top + last.height + TRACK_DIVIDER_HEIGHT;
    }
    isNewTrack = true;
  } else if (previewState.trackIndex !== null) {
    // 기존 트랙에 배치
    const trackLayout = tracksAndLayout[previewState.trackIndex];
    if (trackLayout) {
      top = trackLayout.top;
      height = trackLayout.height;
    }
  }

  const baseColor = getColorByContentType(previewState.contentType);
  // 배치 불가능하면 빨간색으로 표시
  const color = previewState.isAvailable ? baseColor : '#ef4444';

  // 새 트랙 생성 시 강조 색상
  const newTrackAccentColor = '#3b82f6'; // blue-500

  const style: React.CSSProperties = {
    position: 'absolute',
    left: roundedLeft + TIMELINE_HORIZONTAL_PADDING,
    top: top + TIMELINE_HEADER_HEIGHT,
    width: Math.max(roundedWidth, 4), // 최소 4px 너비
    height,
    background: isNewTrack
      ? `${newTrackAccentColor}44` // 새 트랙은 파란색 배경
      : previewState.isAvailable
        ? `${color}66`
        : `${color}44`, // 40-60% opacity
    borderRadius: 4,
    border: isNewTrack
      ? `2px dashed ${newTrackAccentColor}`
      : `2px ${previewState.isAvailable ? 'dashed' : 'solid'} ${color}`,
    pointerEvents: 'none',
    zIndex: 100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  };

  const labelStyle: React.CSSProperties = {
    color: '#fff',
    fontSize: 11,
    fontWeight: 500,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    padding: '0 4px',
    textShadow: '0 1px 2px rgba(0,0,0,0.5)',
  };

  // 라벨 텍스트 결정
  const getLabelText = () => {
    if (!previewState.isAvailable) {
      return '배치 불가 · 같은 종류의 빈 트랙이나 새 트랙에 놓으세요';
    }
    if (previewState.estimatedDuration)
      return `예상 길이 · ${previewState.name}`;
    if (isNewTrack) {
      return `+ ${previewState.name}`;
    }
    return previewState.inserted
      ? `삽입 · ${previewState.name}`
      : previewState.name;
  };

  return (
    <div
      className="pointer-events-none absolute inset-0 overflow-hidden"
      style={{ zIndex: 100 }}
    >
      <div style={style}>
        {roundedWidth > 40 && <span style={labelStyle}>{getLabelText()}</span>}
      </div>
    </div>
  );
};
